import { initLocalization, loc } from '@/__core__/localization/localization'
import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { allSettled } from '@/__core__/utils/fetch'
import { defineStore } from 'pinia'
import { formatDate } from '@/__core__/components/headers/common-header/CommonHeader.helpers'
import { FALLBACK_STAGES } from './Knockout.constants'

const THIRD_PLACE_IDENTIFIERS = ['3rd_place_final', '3rd_place_playoff']
const TBD = 'TBD'
const ALLOWED_STAGES = ['round_of_16', 'quarterfinal', 'semifinal', 'final']

/** @typedef {{ sesaonId: string, sportType: string, region?: string, languageIdentifier: string }} FetchPayload */
/** @typedef {{ id: string, name: string, shortName: string, logoUrl: string, score?: number, virtual: boolean }} Team */
/** @typedef {{ eventId: string, startTime: string, order: number, teams: Array<Team>, winnerId: string, live: boolean, matchStatus: string }} Event */
/** @typedef {{ roundId: string, stage: string, localizedStageName: string, order: number, winnerId?: string, events: Array<Event> }} KnockoutNode */

// REFACTOR: write helpers instead of coupling logic with store
export const useKnockoutStore = defineStore('knockout', {
  state: () => ({
    seasonId: '',
    competition: {},
    name: '',
    duration: '',
    tabs: [],
    mergedTabs: [],

    /** @type {Array<KnockoutNode>} stages */
    stages: FALLBACK_STAGES,
    /** @type {Array<KnockoutNode>} nodes */
    nodes: [],
    stageDurationStripe: [],
    drfRetryCounter: 0
  }),

  getters: {
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    },

    adjustedTabs:
      state =>
      (useMergedTabs = false) =>
        !useMergedTabs ? state.tabs : state.mergedTabs
  },

  actions: {
    getShouldCallApi() {
      // is any event live, if so, call api
      return this.nodes.some(node => node.events.some(event => event.live || Math.abs(Date.now() - new Date(event.startTime).getTime()) <= 5 * 60 * 1000))
    },

    /** @param {FetchPayload} payload */
    async fetchKnockout(payload) {
      const API = payload.sportType === 'tennis' ? 'TennisDraw' : 'Knockout'
      try {
        const [, knockoutResponse] = await allSettled([initLocalization(payload.languageIdentifier), SportsModeServiceRepository[API].get(payload)])

        if (!knockoutResponse) {
          throw new Error('Knockout data not found')
        }

        this.updateKnockout(knockoutResponse.data, payload.languageIdentifier)

        return false
      } catch (error) {
        throw new Error(`[Knockout.store, fetchKnockout]: ${error.message}`)
      }
    },

    updateKnockout(knockoutResponseData, langId) {
      this.competition = knockoutResponseData.competition
      this.seasonId = knockoutResponseData.seasonId
      this.name = knockoutResponseData.groupName

      /** @type {Array<KnockoutNode>} */
      const knockoutNodes = knockoutResponseData.nodes
      this.nodes = knockoutNodes

      const supported = knockoutNodes?.some(node => ALLOWED_STAGES.includes(node.stage)) ?? false

      if(this.nodes.length === 1 || this.nodes?.[0]?.stage === 'final' || !supported) {
        throw new Error(`[Knockout.store, updateKnockout]: competition is not suported buy knockout format`)
      }

      let tabs = {}
      let stages = {}

      for (const node of knockoutNodes) {
        if(!ALLOWED_STAGES.includes(node.stage) && !THIRD_PLACE_IDENTIFIERS.includes(node.stage)) continue

        const isThridPlaceNode = THIRD_PLACE_IDENTIFIERS.includes(node.stage)

        if (!isThridPlaceNode) {
          tabs[node.stage] = {
            id: node.stage,
            name: loc(`sm_${node.stage}`, langId),
            value: node.stage
          }
        } else {
          // it is required to reassign node stage to be 3rd_place_final because API provides different enums
          node.stage = '3rd_place_final'
        }

        let targetStage = node.stage

        if (isThridPlaceNode) {
          node.order = 999999
          targetStage = 'final'
        }

        if(node.events && !node.events.length) {
          continue
        }
        
        if (!stages[targetStage]) {
          stages[targetStage] = []
        }

        stages[targetStage].push(node)
      }

      if(!stages.quarterfinal){
        stages = {
          ...stages,
          'quarterfinal': [{
            roundId: 'missing',
            stage: 'quarterfinal',
            order: 1
          },
          {
            roundId: 'missing',
            stage: 'quarterfinal',
            order: 2
          },
          {
            roundId: 'missing',
            stage: 'quarterfinal',
            order: 3
          },
          {
            roundId: 'missing',
            stage: 'quarterfinal',
            order: 4
          }]
        }

        tabs = {
          ...tabs,
          'quarterfinal': {
            id: 'quarterfinal',
            name: loc(`sm_quarterfinal`, langId),
            value: 'quarterfinal'
          }
        }
      }

      if(!stages.semifinal){
        stages = {
          ...stages,
          'semifinal': [{
            roundId: 'missing',
            stage: 'semifinal',
            order: 1
          },
          {
            roundId: 'missing',
            stage: 'semifinal',
            order: 2
          }]
        }

        tabs = {
          ...tabs,
          'semifinal': {
            id: 'semifinal',
            name: loc(`sm_semifinal`, langId),
            value: 'semifinal'
          }
        }
      }

      if (stages.final) {
        stages.final = stages.final.sort((a, b) => a.order - b.order)
      } else {
        stages = {
          ...stages,
          'final': [{
            roundId: 'missing',
            stage: 'final',
            order: 1
          }]
        }
        tabs = {
          ...tabs,
          'final': {
            id: 'final',
            name: loc(`sm_final`, langId),
            value: 'final'
          }
        }
      }

      this.tabs = Object.values(tabs).map((tab, index) => ({ ...tab, index }))

      this.mergedTabs = this.tabs.reduce((acc, curr, index) => {
        if (curr.id === 'final') {
          return acc
        } else if (curr.id === 'semifinal') {
          const name = tabs[curr.id].name
          acc.push({ ...curr, id: 'smifinal_and_final', name: `${curr.name} / ${name}`, index })
        } else {
          acc.push(curr)
        }

        return acc
      }, [])

      this.stages = Object.values(stages)

      this.stageDurationStripe = this.stages.reduce((acc, stage) => {
        if (!stage || !Array.isArray(stage) || stage.length <= 0) {
          return acc
        }

        const sortedStage = stage[0]?.startTime ? stage.toSorted((nodeA, nodeB) => new Date(nodeA.events[0]?.startTime) - new Date(nodeB.events[0]?.startTime)) : stage

        const [firstRound] = [sortedStage[0], sortedStage[sortedStage.length - 1]]

        const labelKey = THIRD_PLACE_IDENTIFIERS.includes(firstRound.stage) ? 'sm_final' : `sm_${firstRound.stage}`
        const { minStartTime, maxEndTime } = firstRound.roundId !== 'missing' ? getMinMax(sortedStage) : { minStartTime: null, maxEndTime: null }

        const durationStart = minStartTime ? formatDate(new Date(minStartTime), langId) : TBD
        const durationEnd = maxEndTime ? formatDate(new Date(maxEndTime), langId) : TBD

        acc.push({
          id: firstRound.stage,
          name: loc(labelKey, langId),
          startTime: minStartTime,
          endTime: maxEndTime,
          duration: `${durationStart} - ${durationEnd}`
        })

        return acc
      }, [])

      let minStartTime = null
      let maxEndTime = null
      for (const event of this.stageDurationStripe) {
        if (event.startTime && (minStartTime === null || event.startTime < minStartTime)) {
          minStartTime = event.startTime
        }
    
        if (event.endTime && (maxEndTime === null || event.endTime > maxEndTime)) {
          maxEndTime = event.endTime
        }
      }

      const startTime = formatDate(new Date(minStartTime), langId)
      const endTime = formatDate(new Date(maxEndTime), langId)
      this.duration = `${startTime} - ${endTime}`
    },

    /** @param {Payload} payload */
    async scheduleKnockoutDataRefresh(payload) {
      try {
        const [knockoutResponse] = await allSettled([SportsModeServiceRepository.Knockout.get(payload)])

        if (!knockoutResponse) {
          throw new Error('Failed to refresh knockout data!')
        }

        this.updateKnockout(knockoutResponse.data, payload.languageIdentifier)
      } catch (error) {
        if (this.drfRetryCounter >= 10) {
          throw new Error(`[Knockout.store, scheduleKnockoutDataRefresh]: ${error.message}`)
        } else {
          this.drfRetryCounter++
        }
      }
    }
  }
})

const getMinMax = sortedStage => {
  let minStartTime = null
  let maxEndTime = null

  for (const item of sortedStage) {
    const event = item.events?.[0]
    if (event.startTime && (minStartTime === null || event.startTime < minStartTime)) {
      minStartTime = event.startTime
    }

    if (event.startTime && (maxEndTime === null || event.startTime > maxEndTime)) {
      maxEndTime = event.startTime
    }
  }

  return { minStartTime, maxEndTime }
}
